import { Controller } from "@hotwired/stimulus";

/**
 * Static Mesh Gradient Controller
 *
 * Creates a static mesh gradient background using the Paper Design shader library.
 * Supports dark mode by checking the data-theme attribute on the html element.
 * Watches for theme changes and updates shader colors dynamically.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["canvas"];

  declare readonly canvasTarget: HTMLElement;
  declare readonly hasCanvasTarget: boolean;

  private shaderMount: any = null;
  private themeObserver: MutationObserver | null = null;
  private getShaderColorFromString: ((color: string) => number[]) | null = null;

  // Light mode colors
  private lightColors = ["#cac7bf", "#d3d1ca", "#c4f0a3", "#e2e0da"];

  // Light mode shader settings
  private lightSettings = {
    u_positions: 48,
    u_waveX: 0.77,
    u_waveXShift: 0.25,
    u_waveY: 0.96,
    u_waveYShift: 1,
    u_mixing: 0.42,
    u_scale: 0.8,
    u_rotation: 360,
    u_offsetX: -0.08,
    u_offsetY: 0,
  };


  // Dark mode colors (3 colors for a different effect)
  private darkColors = ["#3d3a35", "#4a4741", "#c5f0a4","#3d3a35"];

  // Dark mode shader settings (different positions/effect)
  private darkSettings = {
    u_positions: 51,
    u_waveX: 0.77,
    u_waveXShift: 0.25,
    u_waveY: 0.96,
    u_waveYShift: 1,
    u_mixing: 0.7,
    u_scale: 0.8,
    u_rotation: 360,
    u_offsetX: -0.08,
    u_offsetY: 0,
  };

  async connect(): Promise<void> {
    const container = this.hasCanvasTarget ? this.canvasTarget : this.element;
    await this.initShader(container);
    this.setupThemeObserver();
    this.setupSystemThemeListener();
  }

  disconnect(): void {
    if (this.shaderMount?.dispose) {
      this.shaderMount.dispose();
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          this.updateShaderColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  private setupSystemThemeListener(): void {
    window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener("change", () => {
      const savedTheme = localStorage.getItem("lumenTheme");
      if (!savedTheme || savedTheme === "system") {
        this.updateShaderColors();
      }
    });
  }

  private updateShaderColors(): void {
    if (this.shaderMount?.setUniforms && this.getShaderColorFromString) {
      const colors = this.getColors().map((c) => this.getShaderColorFromString!(c));
      const settings = this.getSettings();
      this.shaderMount.setUniforms({
        u_colors: colors,
        u_colorsCount: colors.length,
        ...settings,
      });
    }
  }

  private isDarkMode(): boolean {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }
    const savedTheme = localStorage.getItem("lumenTheme");
    if (savedTheme === "dark") return true;
    if (savedTheme === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getColors(): string[] {
    return this.isDarkMode() ? this.darkColors : this.lightColors;
  }

  private getSettings(): typeof this.lightSettings {
    return this.isDarkMode() ? this.darkSettings : this.lightSettings;
  }

  private async initShader(container: HTMLElement): Promise<void> {
    try {
      const {
        ShaderMount,
        staticMeshGradientFragmentShader,
        getShaderColorFromString,
      } = await import("@paper-design/shaders");

      // Store reference for dynamic color updates
      this.getShaderColorFromString = getShaderColorFromString;

      const colors = this.getColors().map((c) => getShaderColorFromString(c));
      const settings = this.getSettings();

      this.shaderMount = new ShaderMount(
        container,
        staticMeshGradientFragmentShader,
        {
          u_colors: colors,
          u_colorsCount: colors.length,
          u_grainMixer: 0.06,
          u_grainOverlay: 0,
          ...settings,
        },
        { alpha: true, premultipliedAlpha: true },
        0 // speed = 0 for static
      );
    } catch (error) {
      console.error("Failed to initialize static mesh gradient shader:", error);
    }
  }
}
